import type { FC, RefObject } from 'react';
import { Fragment } from 'react';
import { Credits } from '../Credits';
import { Button } from '../ui';
import { UserAvatar } from '../UserAvatar/UserAvatar';
import { useNavigation } from '../../contexts/NavigationContext';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { cn } from '../../utils/cn';
import { FaCrown } from '../icons';
import { seatsBySlot, sideOf } from '../caseBattle/battleModel';
import { ReactionCounts } from '../caseBattle/ReactionCounts';
import { DROP_FLIGHT_MS } from '../caseBattle/showTiming';
import type { CardShowState } from '../caseBattle/cardShowState';
import type { BattleData } from './BattleCard';
import translations from './BattleCard.translations';

interface CardFooterProps {
  battle: BattleData;
  show: Pick<CardShowState, 'running' | 'flight' | 'flightColor' | 'flightElapsed' | 'flying' | 'bank'>;
  catcherRef: RefObject<HTMLSpanElement | null>;
  onOpen?: (battleId: string) => void;
  onJoin?: (battleId: string) => void;
}

export const winnerSlots = (battle: BattleData): Set<number> => {
  if (!('winnerId' in battle)) return new Set();
  if (typeof battle.winnerTeam === 'number') {
    const team = battle.winnerTeam;
    return new Set(battle.participants.filter((p) => sideOf(p.slot) === team).map((p) => p.slot));
  }
  if (battle.winnerId) {
    const winnerId = battle.winnerId;
    return new Set(battle.participants.filter((p) => p.userId === winnerId).map((p) => p.slot));
  }
  return new Set();
};

const CARD_BUTTON = 'h-9 px-4 py-0 text-[14px] font-semibold';

const CTA = {
  running: { key: 'watch' },
  COMPLETED: { key: 'results' },
  CANCELLED: { key: 'cancelled' },
} as const;

export const CardFooter: FC<CardFooterProps> = ({ battle, show, catcherRef, onOpen, onJoin }) => {
  const { t } = useLocalTranslation(translations);
  const navigation = useNavigation();
  const seats = seatsBySlot(battle.maxPlayers, battle.participants);
  const winners = winnerSlots(battle);
  const status: string = battle.status;
  const cta = show.running ? CTA.running : status === 'COMPLETED' ? CTA.COMPLETED : status === 'CANCELLED' ? CTA.CANCELLED : null;
  const avatarSize = battle.maxPlayers > 2 ? 's' : 'm';
  const { flight, flightColor, flightElapsed, flying, bank } = show;
  const joinable = onJoin !== undefined && cta === null;

  const footer = (
    <div className={cn('relative flex items-end justify-between gap-3 px-1', !joinable && 'pb-2.5')}>
      <div className="flex min-w-0 flex-col items-start gap-2">
        <div className="flex items-end gap-2">
          {seats.map((seat, slot) => (
            <Fragment key={slot}>
              {battle.isTeam && slot === 2 && (
                <span dir="ltr" className="self-center text-[14px] leading-none text-label-tertiary">
                  ×
                </span>
              )}
              {seat ? (
                <span
                  ref={flight?.slot === slot ? catcherRef : null}
                  className="relative shrink-0 rounded-full"
                  style={
                    flight?.slot === slot
                      ? {
                          animation: flying ? 'shotBattleCatch 700ms ease-out both' : undefined,
                          animationDelay: flying ? `${DROP_FLIGHT_MS * 0.92 - 260 - flightElapsed}ms` : undefined,
                          boxShadow: flying ? undefined : `0 0 0 2px ${flightColor}, 0 0 18px ${flightColor}`,
                        }
                      : undefined
                  }
                >
                  {winners.has(slot) && (
                    <FaCrown size={avatarSize === 's' ? 14 : 18} className="absolute left-1/2 -translate-x-1/2 -top-2.5 text-accent-yellow" />
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigation.navigate('profile', { userId: seat.userId });
                    }}
                    className="block active:opacity-70"
                  >
                    <UserAvatar userId={seat.userId} firstName={seat.firstName ?? null} level={seat.level ?? 1} size={avatarSize} />
                  </button>
                </span>
              ) : (
                <div className={cn('rounded-full border border-dashed border-label-quaternary shrink-0', avatarSize === 's' ? 'w-8 h-8' : 'w-12 h-12')} />
              )}
            </Fragment>
          ))}
        </div>
        <ReactionCounts reactions={battle.reactions} size="s" limit={3} rest={false} reserve />
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1.5">

        {bank > 0 && (
          <span key={bank} className="inline-flex items-baseline gap-1 text-end leading-none [animation:shotBattleCasePop_320ms_cubic-bezier(0.3,0.9,0.3,1)_both]">
            <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-label-tertiary">{t('bank')}</span>
            <span className="text-label-primary text-[15px] font-bold tabular-nums">
              <Credits amount={bank} iconSize={12} compact />
            </span>
          </span>
        )}
        {cta && (
          <Button
            size="m"
            variant="tertiary"
            onClick={(e) => {
              e.stopPropagation();
              onOpen?.(battle.id);
            }}
            className={cn(CARD_BUTTON, 'bg-fill-tertiary', status === 'CANCELLED' && 'text-label-tertiary')}
          >
            {t(cta.key)}
          </Button>
        )}
      </div>
    </div>
  );

  if (!joinable) return footer;

  return (
    <div className="flex flex-col gap-2.5">
      {footer}
      <Button
        size="m"
        stretched
        onClick={(e) => {
          e.stopPropagation();
          onJoin(battle.id);
        }}
        className={cn(CARD_BUTTON, 'mx-1 mb-2.5')}
      >
        {t('join')} <Credits amount={battle.totalCost} iconSize={12} compact />
      </Button>
    </div>
  );
};
